import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useLocation, useNavigate } from 'react-router-dom';
import AnimatedLink from './AnimatedLink';
import UserProfileDropdown from './UserProfileDropdown';
import { Bell, User, Building2, Rocket, ChevronDown, LayoutDashboard, Calendar, Search, Users, Store, HeartHandshake, FileText, MessageSquare, Eye, Folder } from 'lucide-react';
import { isPlatformSystemAdmin } from '../utils/roleUtils';
import CustomerAskAiWidget from './CustomerAskAiWidget';

interface CustomerNavbarProps {
  onLogout?: () => void;
  showDashboardLink?: boolean;
  showLogoutButton?: boolean;
  showAuthButtons?: boolean; // For pages that need SIGN IN/SIGN UP buttons
  unreadNotificationsCount?: number;
  onNotificationsClick?: () => void;
}

const CustomerNavbar: React.FC<CustomerNavbarProps> = ({ 
  onLogout, 
  showDashboardLink = true, 
  showLogoutButton = true,
  showAuthButtons = false,
  unreadNotificationsCount = 0,
  onNotificationsClick
}) => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSignInDropdownOpen, setIsSignInDropdownOpen] = useState(false);
  // IMPORTANT: Keep separate refs for desktop vs mobile containers.
  // Both are rendered in the DOM (one hidden via CSS), and sharing a single ref
  // can cause it to point at the hidden node, breaking outside-click detection.
  const desktopDropdownRef = useRef<HTMLDivElement>(null);
  const mobileDropdownRef = useRef<HTMLDivElement>(null);
  const mobileTabbarRef = useRef<HTMLElement>(null);

  const isPlatformAdmin = Boolean(user && isPlatformSystemAdmin(user));
  
  // Check if we're on customer dashboard pages, search page, or home page
  const isCustomerDashboard = location.pathname.startsWith('/dashboard/customer') || location.pathname === '/search' || location.pathname === '/';
  const showMobileTabbar = isCustomerDashboard && Boolean(user) && showLogoutButton && !isPlatformAdmin;

  useEffect(() => {
    const bar = mobileTabbarRef.current;
    if (!showMobileTabbar || !bar) {
      document.documentElement.style.removeProperty('--customer-mobile-tabbar-height');
      return;
    }
    const apply = () => {
      const height = bar.getBoundingClientRect().height;
      if (height > 0) {
        document.documentElement.style.setProperty('--customer-mobile-tabbar-height', `${height}px`);
      } else {
        document.documentElement.style.removeProperty('--customer-mobile-tabbar-height');
      }
    };
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(bar);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--customer-mobile-tabbar-height');
    };
  }, [showMobileTabbar]);

  const platformAdminNavLinks = isPlatformAdmin ? (
    <div className="hidden sm:flex items-center gap-2 mr-1">
      <AnimatedLink
        to="/blog/"
        className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3.5 py-2 text-sm font-medium text-[#0e1d31] hover:bg-gray-50 hover:border-gray-300 transition-colors"
      >
        <Eye className="h-4 w-4 text-[#31445a]" strokeWidth={2} />
        Public Blog
      </AnimatedLink>
      <AnimatedLink
        to="/dashboard/platform"
        className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3.5 py-2 text-sm font-medium text-[#0e1d31] hover:bg-gray-50 hover:border-gray-300 transition-colors"
      >
        <Folder className="h-4 w-4 text-[#31445a]" strokeWidth={2} />
        Business Platform
      </AnimatedLink>
    </div>
  ) : null;
  
  // Determine active nav link
  const getActiveNavLink = (path: string) => {
    if (path === '/dashboard/customer' && location.pathname === '/dashboard/customer') {
      return true;
    }
    if (path === '/dashboard/customer/appointments' && location.pathname.includes('/appointments')) {
      return true;
    }
    if (path === '/dashboard/customer/quote-requests' && location.pathname.includes('/quote-requests')) {
      return true;
    }
    if (path === '/search' && location.pathname === '/search') {
      return true;
    }
    if (path === '/dashboard/customer/invoices' && location.pathname.includes('/invoices')) {
      return true;
    }
    if (path === '/dashboard/customer/gift-cards' && location.pathname.includes('/gift-cards')) {
      return true;
    }
    return false;
  };

  const getDesktopNavLinkClass = (path: string) => {
    const isActive = getActiveNavLink(path);
    return `px-3 py-2 text-sm transition-colors ${
      isActive
        ? 'text-[#0B4778] font-semibold underline decoration-[#0B4778] decoration-2 underline-offset-[7px]'
        : 'text-gray-600 font-medium hover:text-gray-900'
    }`;
  };

  const handleLogout = async () => {
    if (onLogout) {
      onLogout();
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      const clickedInsideDesktop = !!desktopDropdownRef.current?.contains(target);
      const clickedInsideMobile = !!mobileDropdownRef.current?.contains(target);

      // If the click happened inside either dropdown container, don't treat it as an outside click.
      if (clickedInsideDesktop || clickedInsideMobile) return;

      // Otherwise, close.
      if (isSignInDropdownOpen) {
        setIsSignInDropdownOpen(false);
      }
    };

    if (isSignInDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSignInDropdownOpen]);

  // Handle navigation for dropdown items
  const handleDropdownNavigation = (path: string) => {
    setIsSignInDropdownOpen(false);
    navigate(path);
  };

  // Navigation items for mobile bottom bar
  const mobileNavItems = [
    { path: '/dashboard/customer', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/dashboard/customer/appointments', label: 'Appointments', icon: Calendar },
    { path: '/search', label: 'Book Service', icon: Search },
    { path: '/dashboard/customer/quote-requests', label: 'Quotes', icon: MessageSquare },
    { path: '/dashboard/customer/invoices', label: 'Invoices', icon: FileText },
    // Gift Cards hidden from customer nav for now
    // { path: '/dashboard/customer/gift-cards', label: 'Gift Cards', icon: Gift },
  ];

  return (
    <>
      <nav className="sticky top-0 left-0 right-0 z-20 py-2 lg:py-3 bg-white border-b border-gray-200" style={{ zIndex: 1020 }}>
        <div className="flex items-center justify-between px-4 lg:px-8 max-w-7xl mx-auto">
          <AnimatedLink to="/" className="flex items-center gap-2.5 no-underline" aria-label="AACore Booking home">
            <img
              src="/home/aacore-logo.png"
              alt=""
              className="w-10 h-10 sm:w-[45px] sm:h-[45px] rounded-full flex-shrink-0"
            />
            <span className="leading-tight">
              <strong className="block text-[0.97rem] sm:text-[1.1rem] font-bold text-[#153e59]">
                AACore Booking
              </strong>
              <small className="hidden min-[360px]:block text-[0.66rem] sm:text-xs text-[#566977] mt-px">
                AI-powered business platform
              </small>
            </span>
          </AnimatedLink>
          <div className="flex items-center space-x-4">
          {showAuthButtons ? (
            // Show auth buttons for non-authenticated users (like HomePage)
            user ? (
              <>
                {showLogoutButton && (
                  <>
                    {isCustomerDashboard && !isPlatformAdmin && (
                      <nav className="hidden lg:flex items-center gap-1 mx-4">
                        <AnimatedLink
                          to="/dashboard/customer"
                          className={getDesktopNavLinkClass('/dashboard/customer')}
                        >
                          Dashboard
                        </AnimatedLink>
                        <AnimatedLink
                          to="/dashboard/customer/appointments"
                          className={getDesktopNavLinkClass('/dashboard/customer/appointments')}
                        >
                          Appointments
                        </AnimatedLink>
                        <AnimatedLink
                          to="/dashboard/customer/quote-requests"
                          className={getDesktopNavLinkClass('/dashboard/customer/quote-requests')}
                        >
                          Quote Requests
                        </AnimatedLink>
                        <AnimatedLink
                          to="/search"
                          className={getDesktopNavLinkClass('/search')}
                        >
                          Book Service
                        </AnimatedLink>
                        <AnimatedLink
                          to="/dashboard/customer/invoices"
                          className={getDesktopNavLinkClass('/dashboard/customer/invoices')}
                        >
                          Invoices
                        </AnimatedLink>
                      </nav>
                    )}

                    {platformAdminNavLinks}
                    
                    {onNotificationsClick && (
                      <button
                        onClick={onNotificationsClick}
                        className="relative text-gray-900 hover:text-gray-700 transition-colors duration-200 p-2"
                        aria-label="Notifications"
                      >
                        <Bell className="w-5 h-5" />
                        {unreadNotificationsCount > 0 && (
                          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                            {unreadNotificationsCount > 9 ? '9+' : unreadNotificationsCount}
                          </span>
                        )}
                      </button>
                    )}
                    <UserProfileDropdown
                      className=""
                      buttonClassName="text-gray-900 hover:text-gray-700"
                      dropdownClassName=""
                      showAvatar={true}
                      hideName={true}
                      showDashboardLink={showDashboardLink}
                      showHelpModal={true}
                    />
                  </>
                )}
              </>
            ) : (
              <>
                {/* Desktop: Sign In Dropdown */}
                <div className="hidden md:flex items-center gap-3 relative" ref={desktopDropdownRef}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsSignInDropdownOpen(prev => !prev);
                    }}
                    className="px-5 py-2.5 text-white rounded-full transition-all text-sm font-semibold flex items-center gap-2 shadow-sm hover:shadow-md hover:opacity-90 bg-[#0B4778] hover:bg-[#093a61]"
                  >
                    <User className="h-4 w-4" strokeWidth={2.5} />
                    Sign In
                    <ChevronDown className={`h-4 w-4 transition-transform ${isSignInDropdownOpen ? 'rotate-180' : ''}`} strokeWidth={2.5} />
                  </button>
                  
                  {isSignInDropdownOpen && (
                    <div 
                      className="absolute top-full right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDropdownNavigation('/login?type=customer');
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-3 transition-colors"
                      >
                        <Users className="h-5 w-5 text-gray-600" />
                        <span className="text-sm font-medium text-gray-900">Customer Sign In</span>
                      </button>
                      
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDropdownNavigation('/login?type=business');
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-3 transition-colors"
                      >
                        <Store className="h-5 w-5 text-gray-600" />
                        <span className="text-sm font-medium text-gray-900">Business Sign In</span>
                      </button>
                      
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDropdownNavigation('/login?type=professional');
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-3 transition-colors"
                      >
                        <Building2 className="h-5 w-5 text-gray-600" />
                        <span className="text-sm font-medium text-gray-900">Professional Sign In</span>
                      </button>
                      
                      <div className="border-t border-gray-200 my-1"></div>
                      
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDropdownNavigation('/affiliates');
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-3 transition-colors"
                      >
                        <HeartHandshake className="h-5 w-5 text-gray-600" />
                        <span className="text-sm font-medium text-gray-900">Affiliate Program</span>
                      </button>
                      
                      <div className="border-t border-gray-200 my-1"></div>
                      
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDropdownNavigation('/register');
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-3 transition-colors"
                      >
                        <Rocket className="h-5 w-5 text-gray-600" />
                        <span className="text-sm font-medium text-gray-900">Get Started Free</span>
                      </button>
                    </div>
                  )}
                </div>

                {/* Mobile: Sign In Dropdown */}
                <div className="flex md:hidden items-center gap-3 relative" ref={mobileDropdownRef}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsSignInDropdownOpen(prev => !prev);
                    }}
                    className="px-4 py-2 text-white rounded-full transition-all text-sm font-semibold flex items-center gap-2 shadow-sm hover:shadow-md hover:opacity-90 bg-[#0B4778] hover:bg-[#093a61] whitespace-nowrap"
                  >
                    <User className="h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                    Sign In
                    <ChevronDown className={`h-4 w-4 flex-shrink-0 transition-transform ${isSignInDropdownOpen ? 'rotate-180' : ''}`} strokeWidth={2.5} />
                  </button>
                  
                  {isSignInDropdownOpen && (
                    <div 
                      className="absolute top-full right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDropdownNavigation('/login?type=customer');
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-3 transition-colors"
                      >
                        <Users className="h-5 w-5 text-gray-600" />
                        <span className="text-sm font-medium text-gray-900">Customer Sign In</span>
                      </button>
                      
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDropdownNavigation('/login?type=business');
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-3 transition-colors"
                      >
                        <Store className="h-5 w-5 text-gray-600" />
                        <span className="text-sm font-medium text-gray-900">Business Sign In</span>
                      </button>
                      
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDropdownNavigation('/login?type=professional');
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-3 transition-colors"
                      >
                        <Building2 className="h-5 w-5 text-gray-600" />
                        <span className="text-sm font-medium text-gray-900">Professional Sign In</span>
                      </button>
                      
                      <div className="border-t border-gray-200 my-1"></div>
                      
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDropdownNavigation('/affiliates');
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-3 transition-colors"
                      >
                        <HeartHandshake className="h-5 w-5 text-gray-600" />
                        <span className="text-sm font-medium text-gray-900">Affiliate Program</span>
                      </button>
                      
                      <div className="border-t border-gray-200 my-1"></div>
                      
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDropdownNavigation('/register');
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-3 transition-colors"
                      >
                        <Rocket className="h-5 w-5 text-gray-600" />
                        <span className="text-sm font-medium text-gray-900">Get Started Free</span>
                      </button>
                    </div>
                  )}
                </div>
              </>
            )
          ) : (
            // Show regular dashboard/logout buttons for authenticated pages
            <>
              {showLogoutButton && user && (
                <>
                  {/* Navigation Links - Only show on customer dashboard pages */}
                  {isCustomerDashboard && !isPlatformAdmin && (
                    <nav className="hidden lg:flex items-center gap-1 mx-4">
                      <AnimatedLink
                        to="/dashboard/customer"
                        className={getDesktopNavLinkClass('/dashboard/customer')}
                      >
                        Dashboard
                      </AnimatedLink>
                      <AnimatedLink
                        to="/dashboard/customer/appointments"
                        className={getDesktopNavLinkClass('/dashboard/customer/appointments')}
                      >
                        Appointments
                      </AnimatedLink>
                      <AnimatedLink
                        to="/dashboard/customer/quote-requests"
                        className={getDesktopNavLinkClass('/dashboard/customer/quote-requests')}
                      >
                        Quote Requests
                      </AnimatedLink>
                      <AnimatedLink
                        to="/search"
                        className={getDesktopNavLinkClass('/search')}
                      >
                        Book Service
                      </AnimatedLink>
                      <AnimatedLink
                        to="/dashboard/customer/invoices"
                        className={getDesktopNavLinkClass('/dashboard/customer/invoices')}
                      >
                        Invoices
                      </AnimatedLink>
                    </nav>
                  )}

                  {platformAdminNavLinks}
                  
                  {onNotificationsClick && (
                    <button
                      onClick={onNotificationsClick}
                      className="relative text-gray-900 hover:text-gray-700 transition-colors duration-200 p-2"
                      aria-label="Notifications"
                    >
                      <Bell className="w-5 h-5" />
                      {unreadNotificationsCount > 0 && (
                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                          {unreadNotificationsCount > 9 ? '9+' : unreadNotificationsCount}
                        </span>
                      )}
                    </button>
                  )}
                <UserProfileDropdown
                  className=""
                  buttonClassName="nav-text"
                  dropdownClassName=""
                  showAvatar={true}
                  hideName={true}
                  showDashboardLink={showDashboardLink}
                  showHelpModal={true}
                />
                </>
              )}
            </>
          )}
        </div>
      </div>
    </nav>
    {/* Mobile Bottom Navigation Bar */}
    {isCustomerDashboard && user && showLogoutButton && !isPlatformAdmin && (
      <nav
        ref={mobileTabbarRef}
        className="customer-mobile-tabbar fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-200 lg:hidden shadow-lg"
        style={{
          zIndex: 1030,
          paddingBottom: 'env(safe-area-inset-bottom, 0)'
        }}
      >
        <div className="flex items-center justify-around px-1 py-2">
          {mobileNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = getActiveNavLink(item.path);
            return (
              <AnimatedLink
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center justify-center gap-1 px-2 py-2 rounded-lg transition-all min-w-0 flex-1 ${
                  isActive
                    ? 'text-[#0B4778] bg-[#0B4778]/5'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon 
                  className={`w-5 h-5 transition-colors ${isActive ? 'text-[#0B4778]' : 'text-gray-600'}`} 
                  strokeWidth={isActive ? 2.5 : 2}
                />
                <span className={`text-[10px] sm:text-xs font-medium truncate w-full text-center leading-tight ${isActive ? 'text-[#0B4778] font-semibold' : 'text-gray-600'}`}>
                  {item.label}
                </span>
              </AnimatedLink>
            );
          })}
        </div>
      </nav>
    )}
    {user && location.pathname.startsWith('/dashboard/customer') && <CustomerAskAiWidget />}
    </>
  );
};

export default CustomerNavbar; 